import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { ApiService } from '@app/services/api.service';
import { UserLoginRequest } from '../models/user-model';
import { ApiResponse, LoginResponse } from '../models/api-response';
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  exp: number;
  sub: string;
  email: string;
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = 'api/k_farm/v1/signin';

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiService: ApiService
  ) {}

  login(payload: UserLoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.apiService.post<ApiResponse<LoginResponse>>(this.apiUrl, payload)
      .pipe(
        tap((res) => {
          if (res.success && res.data?.token) {
            localStorage.setItem('token', res.data.token);
          }
        })
      );
  }

  logout(): void {
    localStorage.removeItem('token');
    this.router.navigate(['/authentication/login']);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getRole(): string | undefined {
    const token = this.getToken();
    if (!token) return undefined;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    } catch {
      return undefined;
    }
  }

  hasRole(requiredRoles: string[]): boolean {
    const userRole = this.getRole();
    return !!userRole && requiredRoles.includes(userRole);
  }

  getcurrentUserId(): number | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.sub ? parseInt(decoded.sub, 10) : null;
    } catch {
      return null;
    }
  }

  getCurrentUserEmail(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      return decoded.email || null;
    } catch {
      return null;
    }
  }

  /** Returns the default dashboard route for the current user's role. */
  getDefaultRoute(): string {
    switch (this.getRole()) {
      case 'SuperAdmin':  return '/dashboards/admin';
      case 'Admin':       return '/dashboards/admin';
      case 'Vendor':      return '/dashboards/vendor';
      case 'Customer':    return '/dashboards/customer';
      case 'FarmManager': return '/dashboards/farm-manager-dashboard';
      case 'PrincipalFarmManager': return '/dashboards/farm-manager-dashboard';
      case 'Tester':      return '/dashboards/tester';
      case 'Consultant':  return '/dashboards/consultant';
      default:            return '/authentication/login';
    }
  }
}
